import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { isAdminOrOwner, useCurrentMembership } from '@/lib/permissions';
import { toast } from '@/components/ui/toast';
import { resolveApiError } from '@/lib/api-errors';

interface SupportSessionRequest {
  id: string;
  status: 'pending_platform' | 'pending_church' | 'approved' | 'active';
  reason: string;
}

export const SupportSessionBanner = () => {
  const { t } = useTranslation('support');
  const { t: tErrors } = useTranslation('errors');
  const membership = useCurrentMembership();
  const queryClient = useQueryClient();
  const enabled = isAdminOrOwner(membership);

  const sessions = useQuery({
    queryKey: ['support', 'session-requests'],
    queryFn: () => api.get<{ data: SupportSessionRequest[] }>('/support/session-requests'),
    enabled,
    refetchInterval: 30_000,
  });

  const decide = useMutation({
    mutationFn: (input: { id: string; action: 'approve' | 'decline' | 'end' }) =>
      api.post(`/support/session-requests/${input.id}/${input.action}`),
    onSuccess: async (_data, input) => {
      if (input.action === 'end') toast.success(t('session.ended'));
      await queryClient.invalidateQueries({ queryKey: ['support', 'session-requests'] });
    },
    onError: (err) => toast.error(resolveApiError(err, tErrors)),
  });

  if (!enabled) return null;
  const rows = sessions.data?.data ?? [];
  const pending = rows.find((row) => row.status === 'pending_church');
  const active = rows.find((row) => row.status === 'active');
  if (!pending && !active) return null;

  return (
    <div className="flex flex-col gap-2 border-b border-warning/30 bg-warning/10 px-4 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-6">
      <p className="text-body-sm text-fg">
        {active ? t('session.bannerActive') : t('session.bannerPending')}
      </p>
      {pending ? (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={decide.isPending}
            onClick={() => decide.mutate({ id: pending.id, action: 'decline' })}
          >
            {t('session.decline')}
          </Button>
          <Button
            type="button"
            size="sm"
            loading={decide.isPending}
            onClick={() => decide.mutate({ id: pending.id, action: 'approve' })}
          >
            {t('session.approve')}
          </Button>
        </div>
      ) : null}
      {active ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 self-end sm:self-auto"
          loading={decide.isPending}
          onClick={() => decide.mutate({ id: active.id, action: 'end' })}
        >
          {t('session.end')}
        </Button>
      ) : null}
    </div>
  );
};
